<template>
  <div class="videos">
    <Hero
      class="hero"
      heading="Images"
    />
    <div class="action-bar-container">
      <EditToggle v-if="hasEditTogglePermissions" :callback="editToggle" class="edit-toggle"/>
    </div>
    <TextBlock title="Key visuals for coherent look and feel" />
    <div
      v-for="(topicData, topicKey) in mappedFiles"
      :key="topicKey"
      class="topic-container"
    >
      <Modal
        title="Upload File"
        :show="showModal[topicKey].create"
        @close="showModal[topicKey].create = false">
        <FileUploaderMultiple
          :files="[{
              accepts: '.zip',
              required: true,
              multiple: false,
              canUpdateDisplayName: false,
              onSelectFilesMapper: onSelectFilesMapper,
          }]"
          :topicKey="topicKey"
          :securityLevel="'private'"
          :isReplace="topicData.files.length > 0"
          :unionHash="topicKey"
          :successCallback="filesUpdate"
        >
        </FileUploaderMultiple>
      </Modal>
      <Modal
        title="Delete File"
        :show="showModal[topicKey].remove"
        @close="showModal[topicKey].remove = false">
        <div class="delete-dialog">
          <span class="display-name"><b>Display name:</b> {{ topicData.displayName }}</span>
          <p>Are you sure you want to delete these files?</p>
          <button @click="deleteFiles(topicKey)" class="btn">Delete</button>
        </div>
      </Modal>

      <div :id="'anchor-' + topicKey" class="header-container">
        <div class="header-topic-container">
          <div
            v-if="hasDeletePermissions && topicData.files.length > 0"
            @click="showModal[topicKey].remove = true"
            class="delete-icon icon">
          </div>
          <h2>{{ topicData.displayName }}</h2>
          <div
            v-if="hasCreatePermissions && topicData.files.length === 0"
            @click="showModal[topicKey].create = true"
            class="new-icon icon">
          </div>
        </div>
        <p v-if="topicData.description" v-html="topicData.description"></p>
      </div>
      <div class="surround-all-containers">
        <div
          v-for="file in topicData.files"
          :key="file.key"
        >
          <div class="image-container">
            <div
              v-for="unionFile in file.unionFiles"
              :key="unionFile.key"
            >
              <img :src="unionFile && cacheBustingImageUrl(unionFile.dynamicURL)" />
            </div>
          </div>
          <div class="download">
            <DownloadButton :url="file.dynamicURL">
              Download {{ topicData.displayName }} Pack
            </DownloadButton>
          </div>
        </div>
      </div>
    </div>
    <ViLinks />
  </div>
</template>
<script>
import Hero from '@/components/hero.vue';
import TextBlock from '@/components/text-block.vue';
import ViLinks from '@/components/visual-id-links.vue';
import EditToggle from '@/components/edit-toggle.vue';
import Modal from '@/components/modal.vue';
import FileUploaderMultiple from '@/components/file-uploader-multiple.vue';
import DownloadButton from '../components/download-button.vue';

import DeleteObjects from '@/handlers/deleteObjects';

import generateRandomHash from '@/utils/generateRandomHash';
import zipDecompress from '@/utils/zipDecompress';
import resizeImage from '@/utils/resizeImage';

import MixinPermissions from '@/mixins/mixin-permissions';
import MixinFileListing from '@/mixins/mixin-file-listing';

export default {
  name: 'Videos',
  components: {
    Hero,
    TextBlock,
    ViLinks,
    EditToggle,
    Modal,
    FileUploaderMultiple,
    DownloadButton,
  },
  mixins: [
    MixinPermissions,
    MixinFileListing(
      {
        key_image_3d: {
          displayName: '3D Images',
          files: [],
          description: '',
        },
        key_image_cities: {
          displayName: 'Cities',
          files: [],
          description: '',
        },
        key_image_nature: {
          displayName: 'Nature',
          files: [],
          description: '',
        },
        key_image_people: {
          displayName: 'People',
          files: [],
          description: '',
        },
        key_image_perspective: {
          displayName: 'Perspective',
          files: [],
          description: '',
        },
        key_image_screen_life: {
          displayName: 'Screen Life',
          files: [],
          description: '',
        },
        key_image_tech: {
          displayName: 'Tech Touch',
          files: [],
          description: '',
        },
        key_image_unbranded_creatives: {
          displayName: 'Unbranded creatives',
          files: [],
          description: '',
        },
        key_image_unique_selling_point: {
          displayName: 'Unique selling point',
          files: [],
          description: '',
        },
      },
      'private',
      '',
    ),
  ],
  data() {
    return {
      securityLevel: 'private',
      editMode: false,
      showModal: {},
      mappedFiles: {},
    };
  },
  watch: {
    files: {
      deep: true,
      handler() {
        this.mapFiles();
      },
    },
    mappedFiles: {
      deep: true,
      handler() {
        this.initializeShowModal();
      },
    },
  },
  methods: {
    download(url) {
      window.open(url);
    },
    editToggle(onOrOff) {
      this.editMode = onOrOff;
    },
    fileMapper(files) {
      const mappedFiles = {};
      Object.keys(files).forEach((topicKey) => {
        mappedFiles[topicKey] = {
          ...files[topicKey],
          // here we overwrite the files array from the original object with
          // one where any union file(ie. image) have been mapped to their video file
          files: files[topicKey].files
            .filter(file => file.metadata && file.metadata.type && file.metadata.type === 'application/zip')
            .map(file => ({
              ...file,
              unionFiles: files[topicKey].files
                .filter(icon => icon.metadata && icon.metadata.type && icon.metadata.type.startsWith('image/')),
            })),
        };
      });
      return mappedFiles;
    },
    mapFiles() {
      this.$set(this, 'mappedFiles', this.fileMapper(this.files));
    },
    initializeShowModal() {
      Object.keys(this.mappedFiles).forEach((topicKey) => {
        this.$set(this.showModal, topicKey, {
          remove: false,
          create: false,
        });
      });
    },
    generateHash(length) {
      return generateRandomHash(length);
    },
    cacheBustingImageUrl(url) {
      return `${url}?v=${Date.now()}`;
    },
    filesUpdate() {
      // function can be found found in mixin-file-listing.js
      this.generateFilelist();
    },
    deleteFiles(topicKey) {
      const deleteFileKeys = this.files[topicKey].files.map(file => file.key);
      DeleteObjects(deleteFileKeys, this.filesUpdate);
    },
    async onSelectFilesMapper(files) {
      const allowedExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'tiff'];
      const promises = files.map(zip => zipDecompress(zip, allowedExtensions));
      const results = await Promise.all(promises);

      const resizedImagesPromises = results[0].map(_file => resizeImage(_file));
      const resizedImages = (await Promise.allSettled(resizedImagesPromises))
        .map(_file => _file.value);


      return [...files, ...resizedImages];
    },
  },
};
</script>


  <style lang="stylus" scoped>
  @require '../style/colors'
  .surround-all-containers
    display: flex
    flex-wrap: wrap
    justify-content: center
    gap: 1.5rem
    max-width: 95rem
    margin: 0 auto 5rem auto
    padding: 0 2rem

    .image-container
      display: flex
      flex-direction: row
      flex-flow: wrap
      justify-content: center
      gap: 1rem

      >*
        height: 8rem

  .hero
    margin-bottom: 30px

  .download-links
    display: flex
    flex-direction: column
    padding: 0 60px
    max-width: 100rem
    justify-content: center
    margin: 0 auto 90px auto
  .btn
    margin-top: 20px

  .topic-container
    margin: 60px 0 0 0

  .header-container
    display: flex
    flex-direction: column
    align-items: center
    width: 100%

    .header-topic-container
      display: grid
      grid-template-columns: 1fr 1fr 1fr
      justify-items: center
      align-items: center
      gap: 5rem

      h2
        grid-column: 2
        min-width: 14rem
        text-align: center

      .icon
        position: relative
        width: 2.5rem
        height: 2.5rem
        cursor: pointer
      .new-icon
        background: url('../assets/addButton.svg') no-repeat
        background-size: contain
        grid-column: 3
        justify-self: end
      .delete-icon
        background: url('../assets/delete_document.svg') no-repeat
        background-size: contain
        grid-column: 1
        justify-self: start
        width: 30px
        height: 30px

      .icon:hover
        cursor: pointer

  .action-bar-container
    display: grid
    grid-template-columns: 1fr 1fr 1fr
    justify-items: center
    align-items: center
    margin-bottom: 2rem
    gap: 4rem

    .edit-toggle
      grid-column: 3
      justify-self: end
      align-self: flex-start

  .download
    max-width: 40rem
    margin: 5rem auto 12rem auto


  .delete-dialog
    display: flex
    flex-direction: column
    align-items: center
    gap: 1rem

    .url
    .display-name
      font-size: 15px
      padding: 2rem
      min-width: 65%

    .url
      color: $black80
      background-color: #DEDEDE

    .display-name
      color: $blue
      background-color: $blue30
      color: $primary-color
      background-color: $secondary-color

    p
      font-size: 1.8rem
      margin-bottom: 2rem
      font-family: space-grotesk

    button
      background-color: $primary-color
      color: $white
      font-size: 2rem
      padding: 1.5rem 3rem
      border: none
      border-radius: .5rem
      cursor: pointer
      transition: background-color 0.3s ease-in-out

      &:hover
        background-color: darken($primary-color, 10%)

  </style>
